import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useForm } from "@tanstack/react-form";

import { AsyncPaginate } from "react-select-async-paginate";

import { addServiceSchemaSchema } from "@/zod-schemas/services/add-service-schema";
import { toServiceRequest } from "@/mappers/services/service-form-to-request";

import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "../common/required-input";
import { Separator } from "@/components/ui/separator";

import { toast } from "sonner";
import { useState } from "react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  DURATION_MINUTES,
} from "@/constants/services/services-constants";

import { useCategoryLoadOptions } from "@/hooks/services/use-categories";
import { useUpdateService } from "@/hooks/services/use-services";
import { useStaffLevelOptions } from "@/hooks/staff/use-staff-levels";
import type { Service } from "@/types/services/api-types/service-types";
import type { OptionType } from "@/types/common/load-options-types";
import { getServiceFormValues } from "@/default-form-values/services/default-service-form-values";
import { useIntl } from "react-intl";
import { durationFromMinutes } from "@/lib/utils";

interface EditServiceDialogProps {
  service: Service;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditServiceDialog({
  service,
  open,
  onOpenChange,
}: EditServiceDialogProps) {
  const intl = useIntl();
  const [serverError, setServerError] = useState<string | null>(null);
  const formLoadOptions = useCategoryLoadOptions();
  const { data: staffLevelOptions = [] } = useStaffLevelOptions();
  const updateService = useUpdateService();

  const form = useForm({
    defaultValues: getServiceFormValues(service),
    validators: {
      onSubmit: addServiceSchemaSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const validated = addServiceSchemaSchema.parse(value);
        await updateService.mutateAsync({
          id: service.id,
          data: toServiceRequest(validated),
        });
        toast.success(`Service "${validated.name}" updated successfully`);
        onOpenChange(false);
      } catch (error: any) {
        const statusCode = error?.response?.status;
        
        if (statusCode === 409) {
          setServerError(`A service with the name "${value.name}" already exists`);
        } else {
          toast.error(`Failed to update service "${value.name}"`);
          onOpenChange(false);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          form.reset();
          setServerError(null);
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Service</DialogTitle>
        </DialogHeader>
        <Separator />

        <form
          className="mb-4"
          id="edit-service-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="category"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Category
                      <RequiredInput />
                    </FieldLabel>
                    <AsyncPaginate
                      value={field.state.value}
                      onChange={(newValue) => {
                        field.handleChange(newValue as OptionType | null);
                      }}
                      loadOptions={formLoadOptions}
                      aria-invalid={isInvalid}
                      className="w-full"
                      placeholder="Select category..."
                    />

                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </FieldGroup>

          <Separator className="my-4" />

          <FieldGroup>
            <form.Field
              name="name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Name
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setServerError(null);
                      }}
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {serverError && <FieldError>{serverError}</FieldError>}
                  </Field>
                );
              }}
            />
            <form.Field
              name="description"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>Description</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </FieldGroup>

          <Separator className="my-4" />

          <FieldGroup>
            <div className="font-semibold">Staff Tier Config</div>

            <div className="grid grid-cols-3 gap-4">
              <form.Field
                name="staff_level"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Staff Level
                        <RequiredInput />
                      </FieldLabel>
                      <Select
                        name={field.name}
                        value={field.state.value}
                        onValueChange={(value) => field.handleChange(value)}
                        aria-invalid={isInvalid}
                        disabled={false}
                      >
                        <SelectTrigger id={field.name} className="w-full">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {staffLevelOptions.map((level) => (
                              <SelectItem key={level.value} value={level.value}>
                                {level.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>

                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />

              <form.Field
                name="duration"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Duration
                        <RequiredInput />
                      </FieldLabel>
                      <Select
                        name={field.name}
                        value={field.state.value.toString()}
                        onValueChange={(value) =>
                          field.handleChange(Number(value))
                        }
                        aria-invalid={isInvalid}
                      >
                        <SelectTrigger id={field.name} className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectGroup>
                            {DURATION_MINUTES.map((minutes) => (
                              <SelectItem
                                key={minutes}
                                value={String(minutes)}
                              >
                                {intl.formatDuration(durationFromMinutes(minutes), { format: "short" })}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />
              <form.Field
                name="price"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Price <RequiredInput />
                      </FieldLabel>
                      <div className="relative">
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                        >
                          $
                        </span>
                        <Input
                          id={field.name}
                          name={field.name}
                          type="number"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          aria-invalid={isInvalid}
                          autoComplete="off"
                          className="pl-6"
                        />
                      </div>
                      <FieldDescription>Inc. GST</FieldDescription>
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />
            </div>
          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" form="edit-service-form" className="w-24">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
